import { env } from "../config/env";
import { createClient } from "./http";

const api = createClient(env.AUTH_BASE_URL);

function appendIfPresent(formData, key, value) {
  if (value === undefined || value === null || value === "") return;
  formData.append(key, value);
}

function normalizeAudience(audience) {
  const v = String(audience || "users").trim();
  if (v === "delivery") return "delivery_partners";
  if (v === "warehouse") return "warehouses";
  return v;
}

function isUserAudience(audience) {
  return normalizeAudience(audience) === "users";
}

function basePath(audience) {
  return isUserAudience(audience)
    ? "/api/admin/user-broadcasts"
    : "/api/admin/broadcasts";
}

function buildFormData(payload = {}) {
  const fd = new FormData();

  appendIfPresent(fd, "audience", normalizeAudience(payload.audience));
  appendIfPresent(fd, "title", payload.title?.trim());
  appendIfPresent(fd, "body", payload.body?.trim());
  appendIfPresent(fd, "actionUrl", payload.actionUrl?.trim());
  appendIfPresent(fd, "filterType", payload.filterType || "all");
  appendIfPresent(fd, "state", payload.state);
  appendIfPresent(fd, "city", payload.city);
  appendIfPresent(fd, "warehouseId", payload.warehouseId);
  appendIfPresent(fd, "startAt", payload.startAt);
  appendIfPresent(fd, "endAt", payload.endAt);

  // Email broadcast ON/OFF
  // Dono keys bhej rahe hain taaki backend sendEmail ya send_email dono read kar sake.
  fd.append("sendEmail", payload.sendEmail ? "true" : "false");
  fd.append("send_email", payload.sendEmail ? "true" : "false");

  if (payload.image instanceof File) {
    fd.append("image", payload.image);
  }

  return fd;
}

export const broadcastsService = {
  async create(payload = {}) {
    const audience = normalizeAudience(payload.audience);
    return api.post(basePath(audience), buildFormData({ ...payload, audience }));
  },

  async list(params = {}) {
    const audience = normalizeAudience(params.audience || "users");
    const cleanParams = { ...params };
    delete cleanParams.audience;

    if (!isUserAudience(audience)) {
      cleanParams.audience = audience;
    }

    return api.get(basePath(audience), { params: cleanParams });
  },

  async getById(id, audience = "users") {
    return api.get(`${basePath(audience)}/${id}`);
  },

  async update(id, payload = {}) {
    const audience = normalizeAudience(payload.audience);
    return api.patch(
      `${basePath(audience)}/${id}`,
      buildFormData({ ...payload, audience })
    );
  },

  async remove(id, audience = "users") {
    const normalizedAudience = normalizeAudience(audience);

    if (normalizedAudience === "users") {
      return api.del(`/api/admin/user-broadcasts/${id}`);
    }

    return api.del(`/api/admin/broadcasts/${id}`);
  },

  async listUserBroadcasts(params = {}) {
    return this.list({ ...params, audience: "users" });
  },

  async setUserBroadcastActive(id, isActive) {
    return this.setActive(id, isActive, "users");
  },

  async setActive(id, isActive, audience = "users") {
    const normalizedAudience = normalizeAudience(audience);

    if (normalizedAudience === "users") {
      return api.patch(`/api/admin/user-broadcasts/${id}/active`, {
        isActive,
      });
    }

    return api.patch(`/api/admin/broadcasts/${id}/active`, {
      isActive,
    });
  },

  async getStates(audience = "users") {
    const normalizedAudience = normalizeAudience(audience);

    if (normalizedAudience === "users") {
      return api.get("/api/admin/user-broadcasts/filter-options/states");
    }

    return api.get("/api/admin/broadcasts/filter-options/states", {
      params: { audience: normalizedAudience },
    });
  },

  async getCities(audience = "users", params = {}) {
    const normalizedAudience = normalizeAudience(audience);

    if (normalizedAudience === "users") {
      return api.get("/api/admin/user-broadcasts/filter-options/cities", {
        params,
      });
    }

    return api.get("/api/admin/broadcasts/filter-options/cities", {
      params: { ...params, audience: normalizedAudience },
    });
  },

  async getWarehouses(audience = "users", params = {}) {
    const normalizedAudience = normalizeAudience(audience);

    if (normalizedAudience === "users") {
      return api.get("/api/admin/user-broadcasts/filter-options/warehouses", {
        params,
      });
    }

    return api.get("/api/admin/broadcasts/filter-options/warehouses", {
      params: { ...params, audience: normalizedAudience },
    });
  },
};

export function buildBroadcastImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env.AUTH_BASE_URL || "";
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}